/**
 * Admin → Teams: the roster's teams (set on each person in the roster).
 * Link each roster person to their planner login so Flow boards shared
 * with a team reach the right people. Admin-only; the server audits
 * every link change.
 */
import { UsersRound, Wand2 } from "lucide-react";
import { useAdminUsers } from "@/hooks/useAdmin";
import {
  useAutoLinkRosterLogins,
  useLinkRosterLogin,
  useRosterLogins,
  type RosterLogin,
} from "@/hooks/useTeams";

const label = (u: { display_name: string | null; email: string }) => u.display_name || u.email;

export function TeamsSection() {
  const { data: rows = [], isLoading } = useRosterLogins();
  const { data: users = [] } = useAdminUsers();
  const link = useLinkRosterLogin();
  const autoLink = useAutoLinkRosterLogins();

  // Group by team (case-insensitive), people with no team last.
  const groups = new Map<string, { team: string | null; people: RosterLogin[] }>();
  for (const r of rows) {
    const key = r.team?.trim().toLowerCase() || "";
    if (!groups.has(key)) groups.set(key, { team: r.team?.trim() || null, people: [] });
    groups.get(key)!.people.push(r);
  }
  const ordered = [...groups.values()].sort((a, b) =>
    a.team === null ? 1 : b.team === null ? -1 : a.team.localeCompare(b.team),
  );
  const logins = users
    .filter((u) => u.status === "active")
    .sort((a, b) => label(a).localeCompare(label(b)));
  const unlinked = rows.filter((r) => r.user_id === null).length;

  return (
    <section data-testid="teams-section">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">Teams</h2>
      <div className="rounded-xl border border-line bg-surface p-5">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-ink-2">
            Teams come from the roster. Link each person to their planner login. Flow boards
            shared with a team are open to its linked people.
          </p>
          <button
            onClick={() => autoLink.mutate(undefined)}
            disabled={autoLink.isPending || unlinked === 0}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-line-strong px-3 py-1.5 text-sm text-ink-2 hover:bg-raised disabled:opacity-50"
            title="Link unlinked people to the login with exactly their name"
          >
            <Wand2 className="h-4 w-4" />
            Link by name
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {ordered.map((g) => (
            <div key={g.team ?? ""} className="rounded-lg border border-line">
              <div className="flex items-center gap-2 border-b border-line-soft px-3 py-2">
                <UsersRound className="h-4 w-4 shrink-0 text-ink-subtle" />
                <span className="flex-1 text-sm font-semibold text-ink">{g.team ?? "No team"}</span>
                <span className="text-xs text-ink-muted">
                  {g.people.filter((p) => p.user_id !== null).length} of {g.people.length} linked
                </span>
              </div>
              <div className="divide-y divide-line-soft">
                {g.people.map((p) => (
                  <div key={p.name} className="flex items-center gap-3 px-3 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm text-ink-2">{p.name}</span>
                    <select
                      value={p.user_id ?? ""}
                      onChange={(e) =>
                        link.mutate({ name: p.name, user_id: e.target.value ? Number(e.target.value) : null })
                      }
                      aria-label={`Planner login for ${p.name}`}
                      className="w-64 rounded border border-line bg-surface px-1.5 py-1 text-xs text-ink-2 outline-none focus:border-indigo-400"
                    >
                      <option value="">Not linked</option>
                      {logins.map((u) => (
                        <option key={u.id} value={u.id}>
                          {label(u)}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!isLoading && rows.length === 0 && (
            <p className="text-sm text-ink-muted">The roster is empty. Add people on the Roster page.</p>
          )}
        </div>
      </div>
    </section>
  );
}
